import React, { useState } from 'react';
import { Crown, Check, Loader2, CreditCard, Clock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { STRIPE_PRODUCTS } from '../stripe-config';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const Subscription: React.FC = () => {
  const { user } = useAuth();
  const { subscription, loading: subscriptionLoading, getActiveProduct, isActive } = useSubscription();
  const [loadingCheckout, setLoadingCheckout] = useState<string | null>(null);

  const handleSubscribe = async (priceId: string, mode: 'payment' | 'subscription') => {
    if (!user) {
      toast.error('Please sign in to subscribe');
      return;
    }

    setLoadingCheckout(priceId);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('Please sign in to continue');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          price_id: priceId,
          mode: mode,
          success_url: `${window.location.origin}/subscription/success`,
          cancel_url: `${window.location.origin}/subscription`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start checkout');
    } finally {
      setLoadingCheckout(null);
    }
  };

  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading subscription...</span>
        </div>
      </div>
    );
  }

  const activeProduct = getActiveProduct();
  
  const subscriptionProducts = STRIPE_PRODUCTS.filter(p => p.mode === 'subscription');
  const singleTemplateProduct = STRIPE_PRODUCTS.find(p => p.id === 'prod_single_template');

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Premium Subscription Plans
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Access all premium templates with customization options for your brand
          </p>
        </div>

        {/* Current Subscription Status */}
        {user && subscription && (
          <div className="mb-8 p-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Subscription</h3>
            <div className="flex items-center justify-between">
              <div>
                {isActive() && activeProduct ? (
                  <div className="flex items-center space-x-2">
                    <Crown className="h-5 w-5 text-yellow-500" />
                    <span className="font-medium text-gray-900">{activeProduct.name}</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                      Active
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">No active subscription</span>
                  </div>
                )}
              </div>
              {subscription.current_period_end && (
                <div className="text-sm text-gray-500">
                  {subscription.cancel_at_period_end ? 'Expires' : 'Renews'} on{' '}
                  {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Individual Template Option */}
        {singleTemplateProduct && (
          <div className="mb-10 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl shadow-sm border border-blue-200 p-6">
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Quick Access Option
              </h2>
              <p className="text-gray-600">
                Need just a single template for a short time?
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{singleTemplateProduct.name}</h3>
                  <p className="text-gray-600 mt-2">{singleTemplateProduct.description}</p>
                  <div className="flex items-center mt-4 text-gray-700">
                    <Clock className="h-5 w-5 text-blue-500 mr-2" />
                    <span>8-day access period</span>
                  </div>
                </div>
                
                <div className="text-center md:text-right">
                  <div className="mb-3">
                    <span className="text-3xl font-bold text-blue-600">${singleTemplateProduct.price.toFixed(2)}</span>
                    <span className="text-gray-500"> one-time</span>
                  </div>
                  
                  <button
                    onClick={() => handleSubscribe(singleTemplateProduct.priceId, 'payment')}
                    disabled={loadingCheckout === singleTemplateProduct.priceId}
                    className="w-full md:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {loadingCheckout === singleTemplateProduct.priceId ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4" />
                        <span>Unlock for $1</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {subscriptionProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 relative hover:shadow-lg transition-all duration-200"
            >
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                <p className="text-gray-600 mb-6">{product.description}</p>
                
                <div className="mb-6">
                  <span className="text-3xl font-bold text-gray-900">
                    ${product.price.toFixed(2)}
                  </span>
                  {product.interval && (
                    <span className="text-gray-500">/{product.interval}</span>
                  )}
                </div>

                <ul className="mb-8 space-y-3 text-left">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>Access to all premium templates</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>Custom branding and logo options</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>Advanced invoice customization</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>Priority customer support</span>
                  </li>
                </ul>

                <button
                  onClick={() => handleSubscribe(product.priceId, 'subscription')}
                  disabled={loadingCheckout === product.priceId || (isActive() && activeProduct?.id === product.id)}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {loadingCheckout === product.priceId ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : isActive() && activeProduct?.id === product.id ? (
                    <>
                      <Check className="h-4 w-4" />
                      <span>Current Plan</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      <span>Subscribe Now</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {!user && (
          <div className="mt-12 text-center p-6 bg-blue-50 rounded-xl border border-blue-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Sign in to Subscribe
            </h3>
            <p className="text-gray-600 mb-4">
              Create an account or sign in to access premium features
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/login"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Sign In
              </a>
              <a
                href="/signup"
                className="border border-blue-600 text-blue-600 px-6 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors duration-200"
              >
                Create Account
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscription;